/**
 * NOTAM Service
 * 
 * Fetches active and archived NOTAMs via the backend endpoint (POST /weather/fetch-notams).
 * The Notamify API key is stored server-side for security.
 * 
 * Endpoints used:
 *   /v2/notams         — Active NOTAMs (1 credit/page)
 *   /v2/notams/archive — Historical NOTAMs (1 credit/page)
 */

import api from './api';

// ============================================================================
// Types
// ============================================================================

export interface NotamFetchResult {
    status: 'success' | 'error' | 'partial';
    notam_count: number;
    active_count: number;
    archive_count: number;
    categories: Record<string, number>;
    errors: string[];
    icao_codes: string[];
    start_date: string;
    end_date: string;
}

// ============================================================================
// Service
// ============================================================================

/**
 * Fetch NOTAMs for given ICAO codes and date range via backend.
 * 
 * @param icaoCodes - Array of ICAO airport codes
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param includeArchive - Also fetch historical/expired NOTAMs
 * @returns NotamFetchResult with count, categories, and errors
 */
export async function fetchNotamsForProject(
    icaoCodes: string[],
    startDate: string,
    endDate: string,
    includeArchive: boolean = false,
): Promise<NotamFetchResult> {
    const response = await api.post<NotamFetchResult>('/weather/fetch-notams', {
        icao_codes: icaoCodes,
        start_date: startDate,
        end_date: endDate,
        include_archive: includeArchive,
    });
    return response.data;
}
